import { useCallback, useEffect, useState } from 'react';
import { access, type Role, type SaveRole } from '../api/access';
import { IconCircleAlert, IconPencil, IconPlus, IconRefreshCw, IconShieldCheck, IconTrash2 } from '../icons';
import { cleanKey, suggestKey } from '../slug';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, type Column } from '../ui/Table';

/**
 * What jobs exist here, and what each one may do.
 *
 * The permission list comes from the server. It is the product's own surface — the things
 * the software knows how to do — and the role is the customer's word for a job that does
 * some of them. The built-in administrator is shown with its boxes ticked and disabled:
 * an administrator who takes away their own right to manage users has locked everybody out
 * of a system nobody can let them back into.
 */

const blank: SaveRole = { key: '', name: '', description: null, isActive: true, permissions: [] };

export function Roles() {
  const t = useTranslate();
  const [rows, setRows] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [draft, setDraft] = useState<{ id: string | null; values: SaveRole; system: boolean } | null>(null);
  const [doomed, setDoomed] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Set while the key is still following the name. Stops as soon as somebody types a key.
  const [keyFollows, setKeyFollows] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, catalogue] = await Promise.all([access.roles.list(), access.permissions()]);
      setRows(list);
      setPermissions(catalogue.map((p) => p.key));
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const values = draft?.values;
  const set = (change: Partial<SaveRole>) =>
    setDraft((d) => (d ? { ...d, values: { ...d.values, ...change } } : d));

  /**
   * Adds or removes one permission.
   *
   * <p>Works from the previous draft rather than the one this render closed over. Ticking
   * two boxes inside a single render would otherwise have both start from the same list,
   * and the second would quietly throw the first away.</p>
   */
  function toggle(permission: string) {
    setDraft((previous) => {
      if (!previous) return previous;

      const held = previous.values.permissions.includes(permission);

      return {
        ...previous,
        values: {
          ...previous.values,
          permissions: held
            ? previous.values.permissions.filter((p) => p !== permission)
            : [...previous.values.permissions, permission],
        },
      };
    });
  }

  async function save() {
    if (!draft || busy) return;

    setBusy(true);
    try {
      const body = {
        ...draft.values,
        key: draft.values.key.trim(),
        name: draft.values.name.trim(),
        description: draft.values.description?.trim() || null,
      };

      if (draft.id) await access.roles.update(draft.id, body);
      else await access.roles.create(body);

      setDraft(null);
      setPanelError(null);
      await reload();
    } catch (e) {
      setPanelError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: Role) {
    setBusy(true);
    try {
      await access.roles.remove(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const canSave = Boolean(
    values &&
      values.name.trim() &&
      (draft?.id || values.key.trim()) &&
      (draft?.system || values.permissions.length > 0),
  );

  /** Why a role cannot be deleted, or nothing if it can. */
  const blocksDelete = (row: Role) =>
    row.isSystem
      ? t('roles.builtInCannotDelete')
      : row.holderCount > 0
        ? t('roles.heldCannotDelete', { count: row.holderCount })
        : null;

  const columns: Column<Role>[] = [
    {
      id: 'name',
      header: t('common.name'),
      className: 'is-primary',
      cell: (row) => (
        <>
          <span className="nv-badge-group">
            {row.name}
            {row.isSystem ? <Badge tone="scale" icon={IconShieldCheck}>{t('roles.builtIn')}</Badge> : null}
          </span>
          {row.description ? <div className="nv-hint">{row.description}</div> : null}
        </>
      ),
      skeletonWidth: '56%',
    },
    {
      id: 'key',
      header: t('roles.key'),
      width: '1%',
      cell: (row) => <span className="nv-code">{row.key}</span>,
      skeletonWidth: '72px',
    },
    {
      id: 'permissions',
      header: t('roles.permissions'),
      cell: (row) =>
        row.permissions.length === permissions.length && permissions.length > 0 ? (
          <Badge tone="scale">{t('roles.everything')}</Badge>
        ) : (
          <span className="nv-badge-group">
            {row.permissions.map((p) => (
              <Badge key={p} tone="neutral" shape="square">{t(`permission.${p}`)}</Badge>
            ))}
          </span>
        ),
      skeletonWidth: '86%',
    },
    {
      id: 'holders',
      header: t('roles.holders'),
      align: 'right',
      cell: (row) =>
        row.holderCount > 0 ? row.holderCount : <span className="nv-subtle">{t('common.none')}</span>,
      skeletonWidth: '28px',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => (
        <Badge tone={row.isActive ? 'comment' : 'neutral'}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      skeletonWidth: '62px',
    },
  ];

  function open(row: Role) {
    setPanelError(null);
    setKeyFollows(false);
    setDraft({
      id: row.id,
      system: row.isSystem,
      values: {
        key: row.key,
        name: row.name,
        description: row.description,
        isActive: row.isActive,
        permissions: row.permissions,
      },
    });
  }

  function openNew() {
    setPanelError(null);
    setKeyFollows(true);
    setDraft({ id: null, values: { ...blank }, system: false });
  }

  return (
    <>
      <PageHeader
        crumbs={[t('app.people'), t('nav.roles')]}
        title={t('roles.title')}
        subtitle={t('roles.subtitle')}
        action={
          <Button variant="primary" icon={IconPlus} onClick={openNew}>{t('roles.add')}</Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Table
          caption={t('roles.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={open}
          rowActions={(row) => {
            const blocked = blocksDelete(row);

            return (
              <>
                <IconButton label={t('common.edit')} icon={IconPencil} onClick={() => open(row)} />
                <RowMenu
                  label={t('common.moreActions')}
                  items={[
                    {
                      label: blocked ?? t('common.delete'),
                      icon: IconTrash2,
                      danger: true,
                      disabled: busy || blocked !== null,
                      onSelect: () => setDoomed(row),
                    },
                  ]}
                />
              </>
            );
          }}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconShieldCheck}
              title={t('roles.empty')}
              text={t('roles.emptyText')}
              action={<Button variant="primary" icon={IconPlus} onClick={openNew}>{t('roles.add')}</Button>}
            />
          }
        />
      </Card>

      <Drawer
        open={draft !== null}
        title={t(draft?.id ? 'roles.editTitle' : 'roles.newTitle')}
        onClose={() => setDraft(null)}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!canSave || busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && values ? (
          <>
            <Field label={t('common.name')} required>
              {({ id }) => (
                <TextInput
                  id={id}
                  placeholder="Bölge Müdürü"
                  value={values.name}
                  onChange={(e) =>
                    set({
                      name: e.target.value,
                      ...(keyFollows ? { key: suggestKey(e.target.value) } : {}),
                    })
                  }
                />
              )}
            </Field>

            <Field label={t('roles.key')} hint={draft.id ? t('roles.keyFixed') : t('roles.keyHint')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  value={values.key}
                  disabled={Boolean(draft.id)}
                  // Cleaned as it is typed rather than refused after the form is filled
                  // in. A space became a hyphen anyway once the server had its say; the
                  // only thing the old field added was finding out later.
                  onChange={(e) => {
                    setKeyFollows(false);
                    set({ key: cleanKey(e.target.value) });
                  }}
                />
              )}
            </Field>

            <Field label={t('common.description')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('common.optional')}
                  value={values.description ?? ''}
                  onChange={(e) => set({ description: e.target.value || null })}
                />
              )}
            </Field>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('roles.permissions')}</h3>
              <p className="nv-hint">{draft.system ? t('roles.builtInHint') : t('roles.permissionsHint')}</p>
              {/* Said where the missing thing is: Kaydet stayed grey with no reason given
                  (field test, 2 November). */}
              {!draft.system && values.permissions.length === 0 ? (
                <p className="nv-field__error">{t('roles.needsPermission')}</p>
              ) : null}

              <div className="nv-permission-list">
                {permissions.map((permission) => (
                  <label className="nv-permission" key={permission}>
                    <input
                      type="checkbox"
                      disabled={draft.system}
                      checked={draft.system || values.permissions.includes(permission)}
                      onChange={() => toggle(permission)}
                    />
                    <span>
                      {t(`permission.${permission}`)}
                      <span className="nv-hint">{t(`permission.${permission}.hint`)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isActive}
                disabled={draft.system}
                onCheckedChange={(isActive) => set({ isActive })}
                label={t('roles.isActive')}
              />
              <p className="nv-hint">{t('roles.isActiveHint')}</p>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? remove(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        <p>{t('common.deleteWarning')}</p>
      </ConfirmDialog>
    </>
  );
}
